import React, { useEffect } from 'react';
import { MessageSquareIcon, MicIcon, BrainCircuitIcon, LightbulbIcon, XIcon, MinimizeIcon, MaximizeIcon, BellIcon } from 'lucide-react';
import { useAISalesAssistant } from '../../contexts/AISalesAssistantContext';
import { useChat } from '../../contexts/ChatContext';
import { useRecording } from '../../contexts/RecordingContext';
import { useClosingAssistant } from '../../contexts/ClosingAssistantContext';
import { ChatTab } from './ChatTab';
import { AudioTab } from './AudioTab';
import { ClosingTab } from './ClosingTab';
import { InsightsTab } from './InsightsTab';
import { NotificationBadge } from './NotificationBadge';
export const AISalesAssistant: React.FC = () => {
  const {
    isOpen,
    isMinimized,
    activeFeature,
    hasUnreadInsights,
    closingReadiness,
    setIsOpen,
    setIsMinimized,
    setActiveFeature,
    toggleAssistant,
    getUnreadCount
  } = useAISalesAssistant();
  const {
    recordingState
  } = useRecording();
  const {
    insights
  } = useClosingAssistant();
  const unreadCount = getUnreadCount();
  if (!isOpen) {
    return <>
        <NotificationBadge data-id="element-73" />
        <button onClick={toggleAssistant} className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center hover:bg-blue-700 transition-all z-50" data-id="element-74">
          <BrainCircuitIcon className="h-6 w-6" data-id="element-75" />
          {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-id="element-76">
              {unreadCount}
            </span>}
        </button>
      </>;
  }
  // Render minimized state
  if (isMinimized) {
    return <>
        <NotificationBadge data-id="element-77" />
        <div className="fixed bottom-6 right-6 z-40" data-id="element-78">
          <div className="flex items-center" data-id="element-79">
            {/* Notification badge for new insights */}
            {hasUnreadInsights && <div className="relative mr-2" data-id="element-80">
                <div className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center" data-id="element-81">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-82"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" data-id="element-83"></span>
                </div>
                <button onClick={() => setIsMinimized(false)} className="bg-white p-2 rounded-full shadow-md border border-gray-200 hover:bg-gray-50" data-id="element-84">
                  <BellIcon className="h-5 w-5 text-gray-600" data-id="element-85" />
                </button>
              </div>}
            {/* Minimized assistant button */}
            <button onClick={() => setIsMinimized(false)} className="flex items-center space-x-2 bg-white pl-2 pr-3 py-2 rounded-full shadow-md border border-gray-200 hover:bg-gray-50" data-id="element-86">
              <div className={`h-3 w-3 rounded-full ${closingReadiness === 'high' ? 'bg-green-500' : closingReadiness === 'medium' ? 'bg-yellow-500' : 'bg-red-500'}`} data-id="element-87"></div>
              <BrainCircuitIcon className="h-5 w-5 text-blue-600" data-id="element-88" />
              <span className="text-sm font-medium text-gray-700" data-id="element-89">
                AI Sales Assistant
              </span>
            </button>
          </div>
        </div>
      </>;
  }
  return <>
      <NotificationBadge data-id="element-90" />
      <div className="fixed bottom-6 right-6 w-96 bg-white rounded-lg shadow-xl z-50 border border-gray-200" data-id="element-91">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg" data-id="element-92">
          <div className="flex items-center" data-id="element-93">
            <BrainCircuitIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-94" />
            <h3 className="font-medium text-gray-800" data-id="element-95">AI Sales Assistant</h3>
          </div>
          <div className="flex items-center space-x-1" data-id="element-96">
            <button onClick={() => setIsMinimized(true)} className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded" data-id="element-97">
              <MinimizeIcon className="h-4 w-4" data-id="element-98" />
            </button>
            <button onClick={() => setIsOpen(false)} className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded" data-id="element-99">
              <XIcon className="h-4 w-4" data-id="element-100" />
            </button>
          </div>
        </div>
        {/* Feature tabs */}
        <div className="flex border-b border-gray-200" data-id="element-101">
          <button className={`flex-1 py-2 text-xs font-medium ${activeFeature === 'insights' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveFeature('insights')} data-id="element-102">
            <div className="flex items-center justify-center" data-id="element-103">
              <LightbulbIcon className="h-4 w-4 mr-1" data-id="element-104" />
              <span data-id="element-105">Insights</span>
            </div>
          </button>
          <button className={`flex-1 py-2 text-xs font-medium ${activeFeature === 'chat' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveFeature('chat')} data-id="element-106">
            <div className="flex items-center justify-center" data-id="element-107">
              <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-108" />
              <span data-id="element-109">Chat</span>
            </div>
          </button>
          <button className={`flex-1 py-2 text-xs font-medium ${activeFeature === 'audio' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveFeature('audio')} disabled={recordingState === 'recording'} data-id="element-110">
            <div className="flex items-center justify-center" data-id="element-111">
              <MicIcon className="h-4 w-4 mr-1" data-id="element-112" />
              <span data-id="element-113">Voice</span>
              {recordingState === 'recording' && <span className="ml-1 h-2 w-2 bg-red-500 rounded-full animate-pulse" data-id="element-114"></span>}
            </div>
          </button>
          <button className={`flex-1 py-2 text-xs font-medium ${activeFeature === 'closing' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveFeature('closing')} data-id="element-115">
            <div className="flex items-center justify-center" data-id="element-116">
              <BrainCircuitIcon className="h-4 w-4 mr-1" data-id="element-117" />
              <span data-id="element-118">Closing</span>
            </div>
          </button>
        </div>
        {/* Content area */}
        <div className="h-[32rem]" data-id="element-119">
          {activeFeature === 'insights' && <InsightsTab data-id="element-120" />}
          {activeFeature === 'chat' && <ChatTab data-id="element-121" />}
          {activeFeature === 'audio' && <AudioTab onTranscriptReady={() => setActiveFeature('insights')} data-id="element-122" />}
          {activeFeature === 'closing' && <ClosingTab data-id="element-123" />}
        </div>
      </div>
    </>;
};